import React, { useState } from 'react';
import { useParams } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Database, 
  Settings, 
  Shield,
  HardDrive,
  Users,
  Calendar,
  Wrench,
  Play,
  Pause,
  RotateCcw
} from 'lucide-react';

interface OperatePageParams {
  clientCode: string;
}

export default function OperatePage() {
  const { clientCode } = useParams<OperatePageParams>();
  const [activeTab, setActiveTab] = useState('dmvs');

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-gray-900 flex items-center justify-center gap-3">
          <Settings className="w-10 h-10 text-shiftleft-600" />
          SQL Server Operations Management
        </h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Comprehensive operational management and administration for {clientCode} SQL Server environments.
          Manage databases, security, backups, maintenance, and system operations.
        </p>
        
        {/* Client Badge */}
        <div className="flex justify-center">
          <Badge variant="outline" className="text-lg px-4 py-2">
            Client: {clientCode}
          </Badge>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="database-ops" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Database Operations
          </TabsTrigger>
          <TabsTrigger value="security-mgmt" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Security Management
          </TabsTrigger>
          <TabsTrigger value="backup-restore" className="flex items-center gap-2">
            <HardDrive className="w-4 h-4" />
            Backup & Restore
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="flex items-center gap-2">
            <Wrench className="w-4 h-4" />
            Maintenance
          </TabsTrigger>
        </TabsList>

        {/* Database Operations Tab */}
        <TabsContent value="database-ops" className="space-y-6">
          <DatabaseOperations clientCode={clientCode} />
        </TabsContent>

        {/* Security Management Tab */}
        <TabsContent value="security-mgmt" className="space-y-6">
          <SecurityManagement clientCode={clientCode} />
        </TabsContent>

        {/* Backup & Restore Tab */}
        <TabsContent value="backup-restore" className="space-y-6">
          <BackupRestore clientCode={clientCode} />
        </TabsContent>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance" className="space-y-6">
          <MaintenanceOperations clientCode={clientCode} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Database Operations Component
function DatabaseOperations({ clientCode }: { clientCode: string }) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Database className="w-6 h-6 text-shiftleft-600" />
            Database Operations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-6">
            Manage database operations including creation, configuration, index management, and schema modifications.
          </p>
          
          {/* Database Operations Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Database Management */}
            <Card className="bg-shiftleft-50 border-shiftleft-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-shiftleft-700">
                  <Database className="w-5 h-5" />
                  Database Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Create, configure, and manage databases and their properties.
                </p>
                <div className="space-y-3">
                  <Button variant="outline" size="sm" className="w-full">Create Database</Button>
                  <Button variant="outline" size="sm" className="w-full">Configure Settings</Button>
                  <Button variant="outline" size="sm" className="w-full">Manage Files</Button>
                </div>
              </CardContent>
            </Card>

            {/* Index Management */}
            <Card className="bg-green-50 border-green-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                  <Settings className="w-5 h-5" />
                  Index Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Create, rebuild, reorganize, and optimize database indexes.
                </p>
                <div className="space-y-3">
                  <Button variant="outline" size="sm" className="w-full">Rebuild Indexes</Button>
                  <Button variant="outline" size="sm" className="w-full">Reorganize Indexes</Button>
                  <Button variant="outline" size="sm" className="w-full">Create Indexes</Button>
                </div>
              </CardContent>
            </Card>

            {/* Statistics Management */}
            <Card className="bg-orange-50 border-orange-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                  <RotateCcw className="w-5 h-5" />
                  Statistics Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Update and manage database statistics for optimal query performance.
                </p>
                <div className="space-y-3">
                  <Button variant="outline" size="sm" className="w-full">Update Statistics</Button>
                  <Button variant="outline" size="sm" className="w-full">Auto Update Stats</Button>
                  <Button variant="outline" size="sm" className="w-full">Statistics Report</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}

// Security Management Component
function SecurityManagement({ clientCode }: { clientCode: string }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-red-600" />
            Security Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-6">
            Manage SQL Server security including logins, users, roles, and permissions.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* User Management */}
            <Card className="bg-red-50 border-red-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                  <Users className="w-5 h-5" />
                  User Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Create and manage SQL Server logins and database users.
                </p>
                <div className="space-y-3">
                  <Button variant="outline" size="sm" className="w-full">Create Login</Button>
                  <Button variant="outline" size="sm" className="w-full">Create User</Button>
                  <Button variant="outline" size="sm" className="w-full">Manage Users</Button>
                </div>
              </CardContent>
            </Card>

            {/* Role Management */}
            <Card className="bg-orange-50 border-orange-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                  <Shield className="w-5 h-5" />
                  Role Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Manage database roles and assign permissions to users.
                </p>
                <div className="space-y-3">
                  <Button variant="outline" size="sm" className="w-full">Create Role</Button>
                  <Button variant="outline" size="sm" className="w-full">Assign Roles</Button>
                  <Button variant="outline" size="sm" className="w-full">Role Permissions</Button>
                </div>
              </CardContent>
            </Card>

            {/* Permission Management */}
            <Card className="bg-purple-50 border-purple-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-purple-700">
                  <Settings className="w-5 h-5" />
                  Permissions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Grant and revoke permissions on database objects.
                </p>
                <div className="space-y-3">
                  <Button variant="outline" size="sm" className="w-full">Grant Permissions</Button>
                  <Button variant="outline" size="sm" className="w-full">Revoke Permissions</Button>
                  <Button variant="outline" size="sm" className="w-full">Permission Report</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Backup & Restore Component
function BackupRestore({ clientCode }: { clientCode: string }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <HardDrive className="w-6 h-6 text-green-600" />
            Backup & Restore Operations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-6">
            Manage database backups, restores, and recovery operations.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Backup Operations */}
            <Card className="bg-green-50 border-green-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                  <HardDrive className="w-5 h-5" />
                  Backup Operations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Perform full, differential, and transaction log backups.
                </p>
                <div className="space-y-3">
                  <Button variant="outline" size="sm" className="w-full">Full Backup</Button>
                  <Button variant="outline" size="sm" className="w-full">Differential Backup</Button>
                  <Button variant="outline" size="sm" className="w-full">Log Backup</Button>
                </div>
              </CardContent>
            </Card>

            {/* Restore Operations */}
            <Card className="bg-shiftleft-50 border-shiftleft-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-shiftleft-700">
                  <RotateCcw className="w-5 h-5" />
                  Restore Operations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Restore databases from backup files with various options.
                </p>
                <div className="space-y-3">
                  <Button variant="outline" size="sm" className="w-full">Restore Database</Button>
                  <Button variant="outline" size="sm" className="w-full">Point-in-Time Restore</Button>
                  <Button variant="outline" size="sm" className="w-full">Restore Log</Button>
                </div>
              </CardContent>
            </Card>

            {/* Backup Scheduling */}
            <Card className="bg-purple-50 border-purple-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-purple-700">
                  <Calendar className="w-5 h-5" />
                  Backup Scheduling
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Schedule automated backup jobs and maintenance plans.
                </p>
                <div className="space-y-3">
                  <Button variant="outline" size="sm" className="w-full">Schedule Backups</Button>
                  <Button variant="outline" size="sm" className="w-full">Maintenance Plans</Button>
                  <Button variant="outline" size="sm" className="w-full">Backup History</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Maintenance Operations Component
function MaintenanceOperations({ clientCode }: { clientCode: string }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Wrench className="w-6 h-6 text-orange-600" />
            Maintenance Operations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-6">
            Perform routine maintenance tasks including DBCC checks, job management, and system optimization.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* DBCC Operations */}
            <Card className="bg-orange-50 border-orange-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                  <Wrench className="w-5 h-5" />
                  DBCC Operations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Run database consistency checks and maintenance commands.
                </p>
                <div className="space-y-3">
                  <Button variant="outline" size="sm" className="w-full">DBCC CHECKDB</Button>
                  <Button variant="outline" size="sm" className="w-full">DBCC CHECKTABLE</Button>
                  <Button variant="outline" size="sm" className="w-full">DBCC SHRINKDB</Button>
                </div>
              </CardContent>
            </Card>

            {/* Job Management */}
            <Card className="bg-shiftleft-50 border-shiftleft-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-shiftleft-700">
                  <Play className="w-5 h-5" />
                  Job Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Create, schedule, and manage SQL Server Agent jobs.
                </p>
                <div className="space-y-3">
                  <Button variant="outline" size="sm" className="w-full">Create Job</Button>
                  <Button variant="outline" size="sm" className="w-full">Schedule Job</Button>
                  <Button variant="outline" size="sm" className="w-full">Job History</Button>
                </div>
              </CardContent>
            </Card>

            {/* System Optimization */}
            <Card className="bg-green-50 border-green-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                  <Settings className="w-5 h-5" />
                  System Optimization
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Optimize system performance and configuration settings.
                </p>
                <div className="space-y-3">
                  <Button variant="outline" size="sm" className="w-full">Update Statistics</Button>
                  <Button variant="outline" size="sm" className="w-full">Rebuild Indexes</Button>
                  <Button variant="outline" size="sm" className="w-full">Optimize Config</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}